import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import LeadManagementModal from '@/components/LeadManagementModal';
import ThankYouEmailModal from '@/components/ThankYouEmailModal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, QrCode, Download, Mail, MapPin, Calendar, Clock, Users, Flame, Droplets, Snowflake, Tag, ExternalLink, Paperclip, RefreshCw } from 'lucide-react';
import QRCode from 'react-qr-code';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { formatDateLong } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [event, setEvent] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailFeatureEnabled, setEmailFeatureEnabled] = useState(false);
  const [propertyDescription, setPropertyDescription] = useState('');
  const [propertyFiles, setPropertyFiles] = useState([]);

  const getLeadStatusBadge = (status) => {
    switch (status) {
      case 'hot':
        return { icon: Flame, label: 'Hot Lead', color: 'text-red-600', bgColor: 'bg-red-100' };
      case 'warm':
        return { icon: Droplets, label: 'Warm Lead', color: 'text-orange-600', bgColor: 'bg-orange-100' };
      case 'cold':
        return { icon: Snowflake, label: 'Cold Lead', color: 'text-blue-600', bgColor: 'bg-blue-100' };
      default:
        return { icon: Tag, label: 'Not Tagged', color: 'text-stone-500', bgColor: 'bg-stone-100' };
    }
  };

  useEffect(() => {
    if (agent) {
      fetchEventData();
    }
  }, [eventId, agent]);

  useEffect(() => {
    // Fetch site settings to check if email feature is enabled
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setEmailFeatureEnabled(response.data.email_feature_enabled ?? false);
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const fetchEventData = async (retryCount = 0) => {
    try {
      const isBroker = agent?.role === 'broker';
      const eventEndpoint = isBroker 
        ? `${API}/broker/events/${eventId}`
        : `${API}/events/${eventId}`;
      const visitorsEndpoint = isBroker 
        ? `${API}/broker/events/${eventId}/visitors`
        : `${API}/events/${eventId}/visitors`;
      
      const [eventRes, visitorsRes, qrRes] = await Promise.all([
        axios.get(eventEndpoint, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(visitorsEndpoint, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/events/${eventId}/qr`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setEvent(eventRes.data);
      setVisitors(visitorsRes.data);

      // Use property description from event response
      if (eventRes.data.property_description) {
        setPropertyDescription(eventRes.data.property_description);
      }

      // Fetch property files by address directly
      try {
        if (eventRes.data.property_address) {
          const filesRes = await axios.get(`${API}/files`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { property_address: eventRes.data.property_address }
          });
          setPropertyFiles(filesRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch property files');
      }

      if (qrRes.data?.url) {
        setQrUrl(qrRes.data.url);
      } else {
        // Fallback: construct sign-in URL from current origin
        setQrUrl(`${window.location.origin}/sign-in/${eventId}`);
      }
      setLoading(false);
    } catch (error) {
      console.error('[EventDetail] Error fetching data:', error);
      // Retry once after a short delay if this is the first attempt and it's a 404
      if (retryCount === 0 && error.response?.status === 404) {
        setTimeout(() => fetchEventData(1), 1000);
        return;
      }
      
      if (error.response?.status === 404) {
        toast.error('Event not found');
        navigate(agent?.role === 'broker' ? '/broker' : '/dashboard');
      } else {
        toast.error('Failed to fetch event data');
      }
      setLoading(false);
      console.error(error);
    }
  };

  const handleExport = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Working with Agent', 'Buying Timeline', 'Budget Range', 'Preferred Locations', 'Interested', 'Private Showing', 'Notes', 'Consent', 'Signed In At'],
      ...visitors.map(v => [
        v.name,
        v.email,
        v.phone || '',
        v.working_with_agent || '',
        v.buying_timeline || '',
        v.budget_range || '',
        v.preferred_locations || '',
        v.interested_in_property || '',
        v.request_private_showing ? 'Yes' : 'No',
        v.notes || '',
        v.consent_communication ? 'Yes' : 'No',
        new Date(v.signed_in_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitors-${event.property_address.replace(/[^a-z0-9]/gi, '-')}.csv`;
    a.click();
    toast.success('Visitor list exported');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-stone-500">Loading event...</p>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-12">
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
            <h2 className="text-2xl font-bold text-stone-800 mb-4">Event Not Found</h2>
            <p className="text-stone-600 mb-6">
              This event doesn't exist or you don't have permission to view it.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24]"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Dashboard
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(agent?.role === 'broker' ? '/broker/events' : '/dashboard')}
          className="mb-6 text-stone-600 hover:text-[#1A4D2E]"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          {agent?.role === 'broker' ? 'Back to Events' : 'Back to Dashboard'}
        </Button>
        
        {/* Event Info */}
        <Card className="bg-white p-8 rounded-2xl card-shadow mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>
                {event.property_address}
              </h1>
              
              <div className="space-y-3 text-stone-600">
                <div className="flex items-center gap-3">
                  <Calendar size={20} strokeWidth={1.5} />
                  <span className="text-lg">{formatDateLong(event.date)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={20} strokeWidth={1.5} />
                  <span className="text-lg">{event.time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users size={20} strokeWidth={1.5} />
                  <span className="text-lg">{visitors.length} visitors signed in</span>
                </div>
              </div>
              
              {event.description && (
                <div className="mt-4">
                  <p className="text-stone-600 leading-relaxed">
                    {showFullDescription || event.description.length <= 150
                      ? event.description
                      : `${event.description.substring(0, 150)}...`}
                  </p>
                  {event.description.length > 150 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-[#1A4D2E] hover:text-[#143D24] font-medium text-sm mt-2 underline"
                    >
                      {showFullDescription ? 'View Less' : 'View More'}
                    </button>
                  )}
                </div>
              )}

              {/* Property Attachments */}
              {propertyFiles.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200" data-testid="event-property-files">
                  <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-2">
                    <Paperclip size={14} />
                    Property Attachments ({propertyFiles.length})
                  </p>
                  <div className="space-y-1.5">
                    {propertyFiles.map((f) => (
                      <a
                        key={f.id}
                        href={f.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#1A4D2E] hover:underline font-medium p-2 bg-stone-50 rounded-lg border border-stone-200 hover:bg-stone-100 transition-colors"
                        data-testid={`event-file-link-${f.id}`}
                      >
                        <ExternalLink size={14} className="flex-shrink-0" />
                        {f.original_filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* QR Code */}
            <Card className="bg-stone-50 p-6 rounded-2xl text-center" data-testid="qr-code-container">
              <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-4">Visitor Sign-In</p>
              <div className="bg-white p-4 rounded-lg inline-block">
                <QRCode value={qrUrl} size={180} />
              </div>
              <p className="text-xs text-stone-500 mt-3 max-w-[200px]">Scan to sign in</p>
            </Card>
          </div>
          
          {/* Action Buttons - hide for brokers viewing assigned agent events */}
          {!(agent?.role === 'broker' && event?.agent_id !== agent?.id) && (
          <div className="flex flex-wrap gap-4 mt-6">
            <Button
              data-testid="export-visitors-button"
              onClick={handleExport}
              disabled={visitors.length === 0}
              className="border-2 border-[#1A4D2E] text-[#1A4D2E] bg-transparent rounded-full px-6 py-3 hover:bg-[#1A4D2E]/5"
            >
              <Download className="mr-2" size={18} />
              Export Visitors
            </Button>
            {emailFeatureEnabled && (
              <Button
                data-testid="send-thank-you-button"
                onClick={() => setIsEmailModalOpen(true)}
                disabled={visitors.length === 0}
                className="bg-[#D4A373] text-white rounded-full px-6 py-3 hover:bg-[#C89563] transition-transform hover:scale-105"
              >
                <Mail className="mr-2" size={18} />
                Send Thank You Emails
              </Button>
            )}
            <Button
              data-testid="refresh-event-btn"
              onClick={() => window.location.reload()}
              variant="outline"
              className="border-2 border-stone-300 text-stone-600 rounded-full px-4 py-3 hover:bg-stone-100"
              size="sm"
            >
              <RefreshCw size={18} />
            </Button>
          </div>
          )}
        </Card>

        {/* Visitors List */}
        <Card className="bg-white p-8 rounded-2xl card-shadow">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>Visitors</h2>
          
          {visitors.length === 0 ? (
            <div className="text-center py-12" data-testid="no-visitors-message">
              <Users className="mx-auto mb-4 text-stone-300" size={48} strokeWidth={1.5} />
              <p className="text-lg text-stone-600">No visitors yet</p>
              <p className="text-sm text-stone-500 mt-2">Share the QR code to start collecting sign-ins</p>
            </div>
          ) : agent?.role === 'broker' && event?.agent_id !== agent?.id ? (
            /* Broker View: Clean leads-list style */
            <div className="space-y-4" data-testid="visitors-table">
              {visitors.map((visitor) => (
                <Card key={visitor.id} className="p-4 md:p-6 rounded-xl border border-stone-200 hover:border-[#1A4D2E] hover:shadow-md transition-all" data-testid={`visitor-row-${visitor.id}`}>
                  {/* Mobile Layout */}
                  <div className="md:hidden space-y-3">
                    <div className="flex justify-between items-start">
                      <p className="text-lg font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{visitor.name}</p>
                      <span className="text-xs text-stone-500">
                        {visitor.signed_in_at ? new Date(visitor.signed_in_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-stone-600">
                        <Mail size={14} />
                        <span className="truncate">{visitor.email}</span>
                      </div>
                      {visitor.phone && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <span className="text-sm">{formatPhoneDisplay(visitor.phone)}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2 text-stone-600">
                        <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                        <span className="text-xs">{event?.property_address || '-'}</span>
                      </div>
                    </div>
                    {visitor.notes && (
                      <div className="pt-3 border-t border-stone-100">
                        <p className="text-xs text-stone-500 font-semibold mb-1">Notes</p>
                        <p className="text-sm text-stone-600 italic">{visitor.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:block">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Name</p>
                        <p className="text-lg font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{visitor.name}</p>
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Contact</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-stone-600">
                            <Mail size={14} strokeWidth={1.5} />
                            <span className="text-sm">{visitor.email}</span>
                          </div>
                          {visitor.phone && (
                            <div className="flex items-center gap-2 text-stone-600">
                              <span className="text-sm">{formatPhoneDisplay(visitor.phone)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Agent</p>
                        <p className="text-sm text-stone-600">{event?.agent_name || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Property</p>
                        <div className="flex items-start gap-2 text-stone-600">
                          <MapPin size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{event?.property_address || '-'}</span>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Last Visit</p>
                        <div className="flex items-center gap-2 text-stone-600">
                          <Calendar size={14} strokeWidth={1.5} />
                          <span className="text-sm">
                            {visitor.signed_in_at ? new Date(visitor.signed_in_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {visitor.notes && (
                      <div className="mt-4 pt-4 border-t border-stone-100">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Notes</p>
                        <p className="text-sm text-stone-600 italic">{visitor.notes}</p>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            /* Agent View: Detailed cards with lead management */
            <div className="space-y-4" data-testid="visitors-table">
              {visitors.map((visitor) => {
                const statusBadge = getLeadStatusBadge(visitor.lead_status);
                const StatusIcon = statusBadge.icon;
                
                return (
                <Card key={visitor.id} className="p-6 rounded-xl border border-stone-200 hover:border-[#1A4D2E] hover:shadow-md transition-all" data-testid={`visitor-row-${visitor.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{visitor.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusBadge.bgColor} ${statusBadge.color}`}>
                          <StatusIcon size={12} />
                          {statusBadge.label}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-stone-600">
                        <p>{visitor.email}</p>
                        {visitor.phone && <p>{formatPhoneDisplay(visitor.phone)}</p>}
                      </div>
                    </div>
                    <div className="text-sm text-stone-500">
                      {visitor.signed_in_at ? new Date(visitor.signed_in_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) : 'Date not available'}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-stone-100">
                    {visitor.working_with_agent && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Working with Agent</p>
                        <p className="text-sm text-stone-700 capitalize">{visitor.working_with_agent}</p>
                      </div>
                    )}
                    {visitor.buying_timeline && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Timeline</p>
                        <p className="text-sm text-stone-700 capitalize">{visitor.buying_timeline.replace('-', ' ')}</p>
                      </div>
                    )}
                    {visitor.budget_range && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Budget</p>
                        <p className="text-sm text-stone-700">{visitor.budget_range.replace('-', ' - ')}</p>
                      </div>
                    )}
                    {visitor.preferred_locations && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Preferred Locations</p>
                        <p className="text-sm text-stone-700">{visitor.preferred_locations}</p>
                      </div>
                    )}
                    {visitor.interested_in_property && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Interest Level</p>
                        <p className="text-sm text-stone-700 capitalize">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            visitor.interested_in_property === 'yes' ? 'bg-green-100 text-green-700' :
                            visitor.interested_in_property === 'maybe' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-stone-100 text-stone-600'
                          }`}>
                            {visitor.interested_in_property}
                          </span>
                        </p>
                      </div>
                    )}
                    {visitor.request_private_showing && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Private Showing</p>
                        <p className="text-sm text-green-700 font-semibold">Requested</p>
                      </div>
                    )}
                  </div>
                  
                  {visitor.notes && (
                    <div className="mt-4 pt-4 border-t border-stone-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Notes</p>
                      <p className="text-sm text-stone-600 italic">{visitor.notes}</p>
                    </div>
                  )}
                  
                  {visitor.consent_communication && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                      Consented to communication
                    </div>
                  )}
                  
                  {/* Manage Lead Button */}
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <Button
                      onClick={() => {
                        setSelectedVisitor(visitor);
                        setIsModalOpen(true);
                      }}
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24]"
                      data-testid={`manage-lead-${visitor.id}`}
                    >
                      <Tag size={16} className="mr-2" />
                      Manage Lead & Add Notes
                    </Button>
                  </div>
                </Card>
                );
              })}
            </div>
          )}
        </Card>
        
        {/* Lead Management Modal */}
        <LeadManagementModal
          visitor={selectedVisitor}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={fetchEventData}
          token={token}
        />

        {/* Thank You Email Modal */}
        <ThankYouEmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          eventId={eventId}
          token={token}
          recipientCount={visitors.length > 0 ? new Set(visitors.map(v => v.email)).size : 0}
          propertyDescription={propertyDescription}
          propertyFiles={propertyFiles}
        />
      </div>
    </Layout>
  );
};

export default EventDetail;
